import React, { useEffect } from 'react'
import styled from 'styled-components'
import "../App.css"
import Grid from './DetailGrid'
import ReactGA from 'react-ga4';

function Home(){
  useEffect(() => {
    document.title = "Masnoon Junaid | Full Stack Engineer — Commercial Projects & AI Product Collaboration";
    const description = "Masnoon Junaid is a Full Stack Engineer specializing in AI-enabled SaaS, enterprise web platforms, and cloud-native products. Open to commercial partnerships, startup collaboration, and engineering leadership opportunities.";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', description);
    }

    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    try {
      ReactGA.send({
        hitType: "pageview",
        page: "/",
        title:"Home"
      });
    } catch (error) {
      // Avoid blocking rendering if analytics requests fail.
    }
  }, []);

  return (
    <HomeStyle className="home">
      <header className="hero">
        <div className="hero-inner">
          <div className="geo-shape-1" aria-hidden="true" />
          <div className="geo-shape-2" aria-hidden="true" />
          <div className="geo-shape-3" aria-hidden="true" />
          <div className="geo-shape-4" aria-hidden="true" />
          <div className="geo-shape-5" aria-hidden="true" />
          <div className="geo-shape-6" aria-hidden="true" />
          <div className="geo-shape-7" aria-hidden="true" />
          <div className="geo-shape-8" aria-hidden="true" />
          <div className="geo-shape-9" aria-hidden="true" />
          <div className="geo-line-1" aria-hidden="true" />
          <div className="geo-line-2" aria-hidden="true" />
          <div className="geo-dots" aria-hidden="true" />
          <p className="eyebrow">Full Stack Engineer</p>
          <h1>Masnoon Junaid</h1>
          <p className="summary">
            Results-driven software engineer with 4.5+ years of experience building AI-enabled,
            cloud-ready products across web and mobile.
          </p>
          <div className="cta-row">
            <a className="btn primary" href="mailto:masnoon20@gmail.com">Let&apos;s Build</a>
            {/* <a className="btn ghost" href="https://masnoonjunaid.com" target="_blank" rel="noopener noreferrer">Visit Website</a> */}
          </div>
        </div>
      </header>

      <Grid/>

      <section className="invite-section">
        <div className="invite-inner">
          <div>
            <p className="invite-label">Commercial Collaboration</p>
            <h2>Open to product partnerships, enterprise projects, and startup collaborations.</h2>
            <p>
              If your team is building AI-enabled platforms, SaaS products, or cloud-native experiences, let’s discuss how I can help accelerate delivery with scalable engineering, clean architecture, and commercial impact.
            </p>
          </div>
          <a className="btn secondary" href="mailto:masnoon20@gmail.com">Discuss a project</a>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-grid">
          <div className="footer-card">
            <p className="footer-label">Professional collaboration</p>
            <h3>Delivering commercial-grade software for AI, SaaS and cloud-native product growth.</h3>
          </div>
          <div className="footer-actions">
            <a className="btn primary" href="mailto:masnoon20@gmail.com">Start a conversation</a>
            <div className="contact-row">
              <a href="mailto:masnoon20@gmail.com">masnoon20@gmail.com</a>
              <a href="https://hackerearth.com/@masnoon" target="_blank" rel="noopener noreferrer">HackerEarth</a>
            </div>
            <ul className="social-links">
              <li>
                <a href="https://github.com/masnoonJunaid/" rel="noopener noreferrer" target="_blank" aria-label="Github Profile">
                  <img className="social-img" alt="Github Profile" src="./images/gith.svg"/>
                </a>
              </li>
              <li>
                <a rel="noopener noreferrer" target="_blank" href="https://www.linkedin.com/in/masnoon-junaid/" aria-label="LinkedIn Profile">
                  <img className="social-img" alt="Masnoon Linkedin Profile" src="./images/linkedin.svg"/>
                </a>
              </li>
              <li>
                <a rel="noopener noreferrer" target="_blank" href="https://twitter.com/MasnoonJunaid" aria-label="Twitter Profile">
                  <img className="social-img" alt="Masnoon twitter" src="./images/twitter.svg"/>
                </a>
              </li>
            </ul>
          </div>
        </div>
        <p className="copyright"><b>© 2021-2026</b> — Commercial partner-ready engineering for SaaS, AI, and cloud products.</p>
      </footer>
    </HomeStyle>
  )
}

const HomeStyle = styled.div`
  --ink: #0f2136;
  --muted: #5f7284;
  --surface: #f9fbff;
  --surface-strong: #ffffff;
  --accent: #005fbb;
  --accent-soft: #e7f0ff;
  --shadow: rgba(18, 36, 70, 0.12);

  color: var(--ink);
  background: linear-gradient(180deg, #f6f8fc 0%, #eef4fb 100%);

  .hero {
    padding: 4rem 1.2rem 2.4rem;
  }

  .hero-inner {
    position: relative;
    max-width: 960px;
    margin: 0 auto;
    border-radius: 28px;
    padding: 2.4rem;
    background: var(--surface-strong);
    box-shadow: 0 28px 70px rgba(15, 33, 64, 0.09);
    border: 1px solid rgba(15, 33, 64, 0.06);
    overflow: hidden;
  }

  .hero-inner::before {
    content: "";
    position: absolute;
    top: -120px;
    right: -80px;
    width: 320px;
    height: 320px;
    background: radial-gradient(circle at 50% 50%, rgba(0, 95, 187, 0.06) 0%, transparent 70%);
    pointer-events: none;
  }

  .hero-inner::after {
    content: "";
    position: absolute;
    bottom: -60px;
    left: -40px;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle at 50% 50%, rgba(0, 95, 187, 0.04) 0%, transparent 60%);
    pointer-events: none;
  }

  /* Diagonal flow pattern - top-left to bottom-right */
  .geo-shape-1 {
    position: absolute;
    top: 12%;
    left: 8%;
    width: 56px;
    height: 56px;
    border: 1px solid rgba(0, 95, 187, 0.1);
    border-radius: 12px;
    transform: rotate(45deg);
    pointer-events: none;
  }

  .geo-shape-2 {
    position: absolute;
    top: 28%;
    left: 22%;
    width: 32px;
    height: 32px;
    border: 1px solid rgba(0, 95, 187, 0.08);
    border-radius: 50%;
    pointer-events: none;
  }

  .geo-shape-3 {
    position: absolute;
    top: 45%;
    left: 38%;
    width: 18px;
    height: 18px;
    background: rgba(0, 95, 187, 0.12);
    border-radius: 4px;
    transform: rotate(12deg);
    pointer-events: none;
  }

  .geo-shape-4 {
    position: absolute;
    top: 62%;
    left: 52%;
    width: 40px;
    height: 40px;
    border: 1px solid rgba(0, 95, 187, 0.06);
    border-radius: 8px;
    transform: rotate(-18deg);
    pointer-events: none;
  }

  .geo-shape-5 {
    position: absolute;
    top: 78%;
    left: 65%;
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, rgba(0, 95, 187, 0.1) 0%, transparent 60%);
    border-radius: 4px;
    transform: rotate(30deg);
    pointer-events: none;
  }

  /* Accent dots along the diagonal */
  .geo-shape-6 {
    position: absolute;
    top: 20%;
    left: 15%;
    width: 8px;
    height: 8px;
    background: rgba(0, 95, 187, 0.2);
    border-radius: 50%;
    pointer-events: none;
  }

  .geo-shape-7 {
    position: absolute;
    top: 38%;
    left: 30%;
    width: 6px;
    height: 6px;
    background: rgba(0, 95, 187, 0.25);
    border-radius: 50%;
    pointer-events: none;
  }

  .geo-shape-8 {
    position: absolute;
    top: 55%;
    left: 45%;
    width: 10px;
    height: 10px;
    background: rgba(0, 95, 187, 0.15);
    border-radius: 50%;
    pointer-events: none;
  }

  .geo-shape-9 {
    position: absolute;
    top: 72%;
    left: 58%;
    width: 6px;
    height: 6px;
    background: rgba(0, 95, 187, 0.2);
    border-radius: 50%;
    pointer-events: none;
  }

  /* Connecting diagonal line */
  .geo-line-1 {
    position: absolute;
    top: 15%;
    left: 10%;
    width: 1px;
    height: 180px;
    background: linear-gradient(180deg, rgba(0, 95, 187, 0.12) 0%, rgba(0, 95, 187, 0.04) 50%, rgba(0, 95, 187, 0) 100%);
    transform: rotate(15deg);
    pointer-events: none;
  }

  .geo-line-2 {
    position: absolute;
    top: 25%;
    left: 18%;
    width: 140px;
    height: 1px;
    background: linear-gradient(90deg, rgba(0, 95, 187, 0.08) 0%, rgba(0, 95, 187, 0) 100%);
    transform: rotate(15deg);
    pointer-events: none;
  }

  /* Grid pattern in corner */
  .geo-dots {
    position: absolute;
    top: 8%;
    right: 8%;
    width: 48px;
    height: 48px;
    background-image: radial-gradient(circle, rgba(0, 95, 187, 0.12) 1px, transparent 1px);
    background-size: 10px 10px;
    opacity: 0.5;
    pointer-events: none;
  }

  .eyebrow {
    position: relative;
    z-index: 1;
    letter-spacing: 0.26em;
    text-transform: uppercase;
    font-size: 0.78rem;
    color: var(--accent);
    font-family: "Inter", "Segoe UI", sans-serif;
    margin-bottom: 1rem;
  }

  .hero-inner > *:not(.geo-shape-1):not(.geo-shape-2):not(.geo-shape-3):not(.geo-shape-4):not(.geo-shape-5):not(.geo-shape-6):not(.geo-shape-7):not(.geo-shape-8):not(.geo-shape-9):not(.geo-line-1):not(.geo-line-2):not(.geo-dots) {
    position: relative;
    z-index: 1;
  }

  h1 {
    margin: 0;
    font-size: clamp(2.4rem, 5vw, 4rem);
    line-height: 1.02;
    font-family: "Inter", "Segoe UI", sans-serif;
  }

  .summary {
    margin-top: 1.3rem;
    max-width: 760px;
    color: var(--muted);
    font-size: 1.05rem;
    line-height: 1.85;
    font-family: "Inter", "Segoe UI", sans-serif;
  }

  .cta-row {
    margin-top: 2rem;
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .btn {
    border-radius: 999px;
    padding: 0.9rem 1.6rem;
    text-decoration: none;
    font-size: 0.95rem;
    font-weight: 700;
    transition: transform 220ms ease, box-shadow 220ms ease, background-color 220ms ease;
    font-family: "Inter", "Segoe UI", sans-serif;
  }

  .btn:hover {
    transform: translateY(-2px);
  }

  .btn.primary {
    background: var(--accent);
    color: #ffffff;
    box-shadow: 0 14px 32px rgba(0, 95, 187, 0.18);
  }

  .btn.ghost {
    border: 1px solid rgba(0, 95, 187, 0.24);
    color: var(--accent);
    background: var(--surface);
  }

  .invite-section {
    margin: 3rem auto 0;
    max-width: 960px;
    padding: 0 1.2rem;
  }

  .invite-inner {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 1.4rem;
    background: linear-gradient(180deg, rgba(0, 95, 187, 0.05), rgba(255, 255, 255, 0.95));
    border: 1px solid rgba(0, 95, 187, 0.14);
    border-radius: 24px;
    padding: 1.8rem 2rem;
    align-items: center;
    box-shadow: 0 18px 32px rgba(15, 33, 64, 0.06);
  }

  .invite-label {
    margin: 0 0 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-size: 0.78rem;
    color: var(--accent);
    font-family: "Inter", "Segoe UI", sans-serif;
  }

  .invite-section h2 {
    margin: 0;
    font-size: clamp(1.75rem, 2.4vw, 2.45rem);
    line-height: 1.08;
    font-family: "Inter", "Segoe UI", sans-serif;
  }

  .invite-section p {
    margin-top: 1rem;
    color: var(--muted);
    max-width: 760px;
  }

  .btn.secondary {
    background: #ffffff;
    border: 1px solid rgba(0, 95, 187, 0.18);
    color: var(--accent);
    box-shadow: 0 10px 24px rgba(0, 95, 187, 0.08);
  }

  .footer {
    margin-top: 3rem;
    padding: 3rem 1rem 2rem;
    background: #ffffff;
    border-top: 1px solid rgba(15, 33, 64, 0.08);
  }

  .footer-grid {
    max-width: 960px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 1.8rem;
    align-items: center;
    justify-content: space-between;
  }

  .footer-card {
    text-align: left;
  }

  .footer-label {
    margin: 0 0 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-size: 0.78rem;
    color: var(--accent);
    font-family: "Inter", "Segoe UI", sans-serif;
  }

  .footer-card h3 {
    margin: 0;
    font-size: clamp(1.4rem, 2.2vw, 1.85rem);
    line-height: 1.12;
    font-family: "Inter", "Segoe UI", sans-serif;
    color: var(--ink);
  }

  .footer-actions {
    display: grid;
    gap: 1rem;
    justify-items: end;
    text-align: right;
  }

  .footer-actions .btn.primary {
    width: auto;
    padding: 0.95rem 1.75rem;
  }

  .social-links {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.9rem;
  }

  .social-img {
    width: 36px;
    height: 36px;
    object-fit: cover;
    transition: transform 220ms ease;
  }

  .social-img:hover {
    transform: scale(1.14);
  }

  .contact-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 1rem;
    color: var(--muted);
    font-family: "Inter", "Segoe UI", sans-serif;
  }

  .contact-row a {
    color: var(--accent);
    text-decoration: none;
    border-bottom: 1px dotted rgba(0, 95, 187, 0.35);
  }

  .copyright {
    margin-top: 1.6rem;
    font-family: "Inter", "Segoe UI", sans-serif;
    color: var(--muted);
    font-size: 0.95rem;
  }

  @media screen and (max-width: 920px) {
    .footer-grid {
      grid-template-columns: 1fr;
      text-align: left;
      justify-items: stretch;
    }

    .footer-actions {
      justify-items: start;
      text-align: left;
    }

    .social-links {
      justify-content: flex-start;
    }

    .contact-row {
      justify-content: flex-start;
    }
  }

  @media screen and (max-width: 700px) {
    .hero {
      padding-top: 2.6rem;
    }

    .hero-inner {
      padding: 1.6rem;
      border-radius: 20px;
    }

    .summary {
      font-size: 1rem;
    }
  }
`

export default Home
